import { useCallback, useMemo } from 'react'
import { useFieldsValues } from './useFieldsValues'
import { useFieldsDirty } from './useFieldsDirty'
import type { FieldValues, FormConfig, TextFieldConfig } from './types'
import {
    CheckboxProps,
    FieldProps,
    TextFieldProps,
} from '../../components/common/Form'
import { ErrorStrings, getFieldsErrors } from './utils'
import { useTranslation } from 'react-i18next'

export * from './types'

export const useErrorTranslations = () => {
    const { t } = useTranslation('translations')
    const errorStrings: ErrorStrings = {
        ERR_REQUIRED: t('login_form.errors.required'),
        ERR_INVALID_EMAIL_ADDRESS: t('login_form.errors.invalid_email'),
        ERR_DUPLICATE_REPORT_NAME: t('reporting.save_report.error_duplicated'),
    }

    return errorStrings
}

export const useForm = (
    config: FormConfig,
    onChange?: (values: FieldValues) => void
) => {
    const { initialValues, fieldsValues, setFieldValue, resetFieldsValues } =
        useFieldsValues(config, onChange)
    const errorStrings = useErrorTranslations()
    const fieldsErrors = getFieldsErrors(config, fieldsValues, errorStrings)
    const { isFieldDirty, setFieldDirty, setAllFieldsDirty, resetFieldsDirty } =
        useFieldsDirty(config)

    const clearFields = () => {
        resetFieldsValues()
        resetFieldsDirty()
    }

    const isEdited = useMemo(() => {
        return Object.keys(fieldsValues).some((detailKey) => {
            return initialValues[detailKey] !== fieldsValues[detailKey]
        })
    }, [fieldsValues, initialValues])

    const isValid = useMemo(() => {
        return Object.keys(fieldsErrors).every((fieldName) => {
            const currentFieldErrors = fieldsErrors[fieldName]
            return !Object.keys(currentFieldErrors).length
        })
    }, [fieldsErrors])

    const getConfig = useCallback(
        (fieldName: string) => {
            return config[fieldName]
        },
        [config]
    )

    const getCommonProps = useCallback(
        (fieldName: string): TextFieldProps => {
            const fieldErrors = isFieldDirty(fieldName)
                ? fieldsErrors[fieldName]
                : undefined
            return {
                name: fieldName,
                required: !!config[fieldName].required,
                disabled: config[fieldName].disabled,
                label: config[fieldName].label ?? '',
                errors: fieldErrors,
                onBlur: () => {
                    setFieldDirty(fieldName, true)
                },
            }
        },
        [config, fieldsErrors, isFieldDirty, setFieldDirty]
    )

    const getCheckboxProps = useCallback(
        (fieldName: string): CheckboxProps => {
            const fieldProps = getCommonProps(fieldName)

            return {
                ...fieldProps,
                checked: fieldsValues[fieldName] as boolean,
                onChange: (event) => {
                    setFieldValue(fieldName, event.target.checked)
                },
            }
        },
        [fieldsValues, getCommonProps, setFieldValue]
    )

    const getInputProps = useCallback(
        (fieldName: string): FieldProps => {
            const fieldConfig = config[fieldName] as TextFieldConfig
            const fieldProps = getCommonProps(fieldName)

            const fieldType = fieldConfig.type
            const placeholder = fieldConfig.placeholder
            const isPassword = 'password' === fieldType
            const type = isPassword ? 'password' : fieldType

            return {
                ...fieldProps,
                type,
                value: fieldsValues[fieldName] as string,
                placeholder: placeholder,
                onChange: (value: string) => {
                    setFieldValue(fieldName, value)
                },
            }
        },
        [config, getCommonProps, fieldsValues, setFieldValue]
    )

    return {
        formState: fieldsValues,
        initialValues,
        setFieldValue,
        isValid,
        getCheckboxProps,
        getInputProps,
        clearFields,
        setFieldDirty,
        setAllFieldsDirty,
        getConfig,
        fieldsErrors,
        isEdited,
        isFieldDirty,
        config,
    }
}
